import React, { useState } from 'react';
import { X, QrCode, Mail, Download, CheckCircle, Loader, AlertTriangle, MessageCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { trackDoctorReportSharing } from '../../../hooks/useGoogleAnalytics';

const ShareWithDoctorModal = ({ isOpen, onClose, patientName, reportPeriod, reportText, reportData }) => {
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [doctorEmail, setDoctorEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [whatsappSent, setWhatsappSent] = useState(false);

  // Build a shareable URL that embeds the report data so it works in any new tab/session
  const buildShareableUrl = () => {
    const base = `${window.location?.origin}/doctor-report-view`;
    if (reportData) {
      try {
        const encoded = encodeURIComponent(JSON.stringify(reportData));
        return `${base}?report=${encoded}`;
      } catch (e) {
        // fall back to base URL if data is too large
      }
    }
    return base;
  };

  const shareableUrl = buildShareableUrl();

  const getReportText = () => {
    if (reportText) return reportText;
    return `MyBodyToday — Consultation Summary\n\nPatient: ${patientName}\nPeriod: ${reportPeriod}\n\nPlease view the full report at: ${shareableUrl}\n\nGenerated by MyBodyToday™`;
  };

  const handleWhatsApp = () => {
    const text = getReportText();
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    trackDoctorReportSharing('whatsapp', String(reportPeriod));
    setWhatsappSent(true);
    setTimeout(() => setWhatsappSent(false), 3000);
  };

  const handleEmailSend = async () => {
    if (!doctorEmail?.trim()) {
      setEmailError("Please enter your doctor's email address.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(doctorEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError('');
    setEmailSending(true);
    try {
      const subject = encodeURIComponent(`MyBodyToday Consultation Summary — ${patientName}`);
      const body = encodeURIComponent(
        `Dear Doctor,\n\nPlease find the consultation summary for ${patientName} (Period: ${reportPeriod}) below:\n\n${getReportText()}\n\nView the full interactive report here:\n${shareableUrl}\n\nThis is a patient-generated symptom summary and is not a medical diagnosis.\n\nKind regards,\n${patientName}\n\nGenerated by MyBodyToday™`
      );
      window.open(`mailto:${doctorEmail}?subject=${subject}&body=${body}`, '_blank');
      trackDoctorReportSharing('email', String(reportPeriod));
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 4000);
    } catch {
      setEmailError('Unable to open email client. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('doctor-qr-code');
    if (!svg) return;
    const svgData = new XMLSerializer()?.serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 300;
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx?.fillRect(0, 0, 300, 300);
      ctx?.drawImage(img, 0, 0, 300, 300);
      const a = document.createElement('a');
      a.download = 'doctor-report-qr.png';
      a.href = canvas?.toDataURL('image/png');
      a?.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'qr', label: 'QR Code', icon: QrCode },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">Share with Your Doctor</h2>
            <p className="text-xs text-gray-500 mt-0.5">Choose how to share your consultation summary</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close modal">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold transition-colors ${
                activeTab === tab?.id
                  ? 'text-green-700 border-b-2 border-green-600 bg-green-50' :'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}>
              <tab.icon className="w-3.5 h-3.5" />
              {tab?.label}
            </button>
          ))}
        </div>

        <div className="px-6 py-5">
          {/* WhatsApp Tab */}
          {activeTab === 'whatsapp' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Send your full consultation summary directly to your doctor via WhatsApp.</p>
              <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-800">WhatsApp Direct Share</p>
                    <p className="text-xs text-green-600">Opens WhatsApp with your full report</p>
                  </div>
                </div>
                <p className="text-xs text-green-700">Your complete symptom summary, timeline, triggers, and discussion questions will be shared.</p>
              </div>
              <button
                onClick={handleWhatsApp}
                className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-all ${
                  whatsappSent
                    ? 'bg-green-50 border-2 border-green-200 text-green-700' :'bg-green-500 hover:bg-green-600 text-white'
                }`}>
                {whatsappSent ? <CheckCircle className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
                {whatsappSent ? 'WhatsApp Opened!' : 'Share via WhatsApp'}
              </button>
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <AlertTriangle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">Opens WhatsApp on your device. Your doctor does not need to have the MyBodyToday app.</p>
              </div>
            </div>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Enter your doctor's email address to send them your full consultation summary.</p>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Doctor's Email Address</label>
                <input
                  type="email"
                  value={doctorEmail}
                  onChange={(e) => { setDoctorEmail(e?.target?.value); setEmailError(''); }}
                  placeholder="doctor@clinic.com"
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-green-400 bg-gray-50"
                />
                {emailError && <p className="text-xs text-red-600 mt-1.5">{emailError}</p>}
              </div>
              {emailSent && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <p className="text-xs text-green-700 font-medium">Email client opened! Complete sending from your email app.</p>
                </div>
              )}
              <button
                onClick={handleEmailSend}
                disabled={emailSending}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-xl font-semibold text-sm transition-colors">
                {emailSending ? <Loader className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {emailSending ? 'Opening email...' : 'Send to Doctor'}
              </button>
              <p className="text-xs text-gray-400 text-center">Opens your default email app with the full report pre-filled.</p>
            </div>
          )}

          {/* QR Code Tab */}
          {activeTab === 'qr' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Show this QR code to your clinician. They can scan it to instantly view your full report.</p>
              <div className="flex justify-center p-4 bg-white border-2 border-gray-100 rounded-xl">
                <QRCodeSVG
                  id="doctor-qr-code"
                  value={shareableUrl}
                  size={200}
                  level="M"
                  includeMargin={true}
                  fgColor="#166534"
                />
              </div>
              <button
                onClick={handleDownloadQR}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold text-sm transition-colors">
                <Download className="w-4 h-4" />
                Download QR Code
              </button>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 pb-5">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg p-3">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">Patient-generated summary. Not a medical diagnosis. For clinical reference only.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareWithDoctorModal;
